import {DOCUMENT, ViewportScroller} from "@angular/common";
import {Inject, Injectable, OnDestroy} from "@angular/core";
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationSkipped,
  NavigationStart,
  Router,
  Scroll,
} from "@angular/router";
import {Subscription} from "rxjs";

const ROUTE_CONTENT_SELECTOR = "[data-route-scroll-content]";
const INSTANT_SCROLL_OPTIONS: ScrollOptions = {behavior: "instant"};
const PASSIVE_CAPTURE_OPTIONS: AddEventListenerOptions = {
  capture: true,
  passive: true,
};
const SCROLL_KEYS = new Set([
  "ArrowDown",
  "ArrowUp",
  "End",
  "Home",
  "PageDown",
  "PageUp",
  " ",
]);

@Injectable({providedIn: "root"})
export class ScrollRestorationService implements OnDestroy {
  private readonly browserWindow: Window | null;
  private readonly routerSubscription?: Subscription;
  private resizeObserver?: ResizeObserver;
  private mutationObserver?: MutationObserver;
  private restorationAbortController?: AbortController;
  private restoreFrame?: number;
  private targetPosition?: [number, number];
  private isHistoryNavigation = false;
  private isNavigationInProgress = false;
  private currentNavigationId = 0;
  private historyNavigationPosition?: [number, number];
  private readonly navigationPositions = new Map<number, [number, number]>();
  private readonly activePointerIds = new Set<number>();

  constructor(
    private readonly router: Router,
    private readonly viewportScroller: ViewportScroller,
    @Inject(DOCUMENT) private readonly document: Document
  ) {
    this.browserWindow = this.document.defaultView;
    if (!this.browserWindow) {
      return;
    }

    this.viewportScroller.setHistoryScrollRestoration("auto");
    this.browserWindow.addEventListener(
      "scroll",
      this.rememberCurrentPosition,
      PASSIVE_CAPTURE_OPTIONS
    );
    this.routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.handleNavigationStart(event);
      } else if (event instanceof NavigationEnd) {
        this.handleNavigationEnd(event);
      } else if (
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this.handleStoppedNavigation();
      } else if (event instanceof Scroll) {
        this.handleRouterScroll(event);
      }
    });
  }

  private handleNavigationStart(event: NavigationStart): void {
    this.saveCurrentPosition();
    this.isNavigationInProgress = true;
    this.isHistoryNavigation = event.navigationTrigger === "popstate";
    const restoredNavigationId = event.restoredState?.navigationId;
    this.historyNavigationPosition =
      restoredNavigationId === undefined
        ? undefined
        : this.navigationPositions.get(restoredNavigationId);
  }

  private handleNavigationEnd(event: NavigationEnd): void {
    this.currentNavigationId = event.id;
    this.isNavigationInProgress = false;
  }

  private handleStoppedNavigation(): void {
    this.isNavigationInProgress = false;
    this.isHistoryNavigation = false;
    this.historyNavigationPosition = undefined;
  }

  private handleRouterScroll(event: Scroll): void {
    if (event.routerEvent instanceof NavigationSkipped) {
      return;
    }

    this.stopPendingRestoration();
    const position = this.historyNavigationPosition ?? event.position;
    this.historyNavigationPosition = undefined;
    if (position) {
      this.startBackNavigationRestoration(position);
    } else if (!this.isHistoryNavigation) {
      this.scrollToPosition([0, 0]);
    }
    this.isHistoryNavigation = false;
  }

  private readonly rememberCurrentPosition = (): void => {
    if (!this.isNavigationInProgress) {
      this.saveCurrentPosition();
    }
  };

  private saveCurrentPosition(): void {
    this.navigationPositions.set(
      this.currentNavigationId,
      this.viewportScroller.getScrollPosition()
    );
  }

  private startBackNavigationRestoration(position: [number, number]): void {
    if (position[1] <= 0) {
      this.scrollToPosition(position);
      return;
    }

    this.targetPosition = position;
    this.observePageLayout();
    const abortController = new AbortController();
    this.restorationAbortController = abortController;
    const listenerOptions: AddEventListenerOptions = {
      ...PASSIVE_CAPTURE_OPTIONS,
      signal: abortController.signal,
    };
    this.listenForUserInteraction(listenerOptions);
    this.listenForViewportChanges(listenerOptions);
    this.scheduleRestoration();
  }

  private observePageLayout(): void {
    const routeContent = this.document.querySelector<HTMLElement>(
      ROUTE_CONTENT_SELECTOR
    );

    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(() =>
        this.scheduleRestoration()
      );
      this.resizeObserver.observe(this.document.body);
      if (routeContent) {
        this.resizeObserver.observe(routeContent);
      }
    }

    if (routeContent && typeof MutationObserver !== "undefined") {
      this.mutationObserver = new MutationObserver(() =>
        this.scheduleRestoration()
      );
      this.mutationObserver.observe(routeContent, {
        childList: true,
        subtree: true,
      });
    }
  }

  private listenForViewportChanges(options: AddEventListenerOptions): void {
    if (!this.browserWindow) {
      return;
    }

    this.browserWindow.addEventListener(
      "resize",
      this.scheduleOnLayoutChange,
      options
    );
    this.browserWindow.visualViewport?.addEventListener(
      "resize",
      this.scheduleOnLayoutChange,
      options
    );
  }

  private scheduleRestoration(): void {
    if (
      !this.browserWindow ||
      !this.targetPosition ||
      this.restoreFrame !== undefined
    ) {
      return;
    }

    this.restoreFrame = this.browserWindow.requestAnimationFrame(() => {
      this.restoreFrame = undefined;
      this.restorePositionIfReachable();
    });
  }

  private restorePositionIfReachable(): void {
    if (
      !this.targetPosition ||
      !this.isPositionReachable(this.targetPosition)
    ) {
      return;
    }

    this.scrollToPosition(this.targetPosition);
  }

  private isPositionReachable(position: [number, number]): boolean {
    const scrollingElement = this.document.scrollingElement;
    if (!scrollingElement) {
      return false;
    }

    const maxScrollY = Math.max(
      0,
      scrollingElement.scrollHeight - scrollingElement.clientHeight
    );
    return position[1] <= maxScrollY;
  }

  private scrollToPosition(position: [number, number]): void {
    this.viewportScroller.scrollToPosition(position, INSTANT_SCROLL_OPTIONS);
  }

  private listenForUserInteraction(options: AddEventListenerOptions): void {
    if (!this.browserWindow) {
      return;
    }

    this.browserWindow.addEventListener(
      "pointerdown",
      this.trackPointerStart,
      options
    );
    this.browserWindow.addEventListener(
      "pointermove",
      this.cancelOnPointerDrag,
      options
    );
    this.browserWindow.addEventListener(
      "pointerup",
      this.trackPointerEnd,
      options
    );
    this.browserWindow.addEventListener(
      "pointercancel",
      this.trackPointerEnd,
      options
    );
    this.browserWindow.addEventListener(
      "wheel",
      this.cancelOnUserInteraction,
      options
    );
    this.browserWindow.addEventListener(
      "keydown",
      this.cancelOnScrollKey,
      options
    );
  }

  private stopPendingRestoration(): void {
    this.targetPosition = undefined;
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    this.resizeObserver = undefined;
    this.mutationObserver = undefined;
    this.activePointerIds.clear();
    this.restorationAbortController?.abort();
    this.restorationAbortController = undefined;
    this.cancelScheduledFrame();
  }

  private cancelScheduledFrame(): void {
    if (!this.browserWindow || this.restoreFrame === undefined) {
      return;
    }

    this.browserWindow.cancelAnimationFrame(this.restoreFrame);
    this.restoreFrame = undefined;
  }

  private readonly scheduleOnLayoutChange = (): void => {
    this.scheduleRestoration();
  };

  private readonly cancelOnUserInteraction = (): void => {
    this.stopPendingRestoration();
  };

  private readonly trackPointerStart = (event: PointerEvent): void => {
    this.activePointerIds.add(event.pointerId);
  };

  private readonly trackPointerEnd = (event: PointerEvent): void => {
    this.activePointerIds.delete(event.pointerId);
  };

  private readonly cancelOnPointerDrag = (event: PointerEvent): void => {
    if (this.activePointerIds.has(event.pointerId)) {
      this.stopPendingRestoration();
    }
  };

  private readonly cancelOnScrollKey = (event: KeyboardEvent): void => {
    if (SCROLL_KEYS.has(event.key)) {
      this.stopPendingRestoration();
    }
  };

  ngOnDestroy(): void {
    this.stopPendingRestoration();
    this.routerSubscription?.unsubscribe();
    if (this.browserWindow) {
      this.browserWindow.removeEventListener(
        "scroll",
        this.rememberCurrentPosition,
        PASSIVE_CAPTURE_OPTIONS
      );
      this.viewportScroller.setHistoryScrollRestoration("auto");
    }
  }
}
